import { useEffect, useState } from 'react';
import { ExportCsv, Toolbar, useGridApiContext } from '@mui/x-data-grid-pro';
import Tooltip from '@mui/material/Tooltip';
import { Box, Button, Menu, MenuItem } from '@mui/material';
import FormatLineSpacingIcon from '@mui/icons-material/FormatLineSpacing';
import DensityMediumIcon from '@mui/icons-material/DensityMedium';
import DensitySmallIcon from '@mui/icons-material/DensitySmall';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { Link } from 'react-router-dom';
// Order avoids a PageCard/BasicDataGrid <-> AssetGridColumnDefs circular-import bug.
import { PageCard } from '../../Compounds/CardWithTitle';
import { BasicDataGrid } from '../../Compounds/Table/BasicDataGrid';
import { DEFAULT_TABLE_STYLES } from '../../../utils/styleHelpers';
import { ComponentSpinner } from '../../Compounds/Loading/ComponentSpinner';
import { DefaultRecipientGridColumnDefs } from './RecipientGridColumnDefs';
import { CustomQuickFilter } from '../../Compounds/UpstackDataGrid/CustomQuickFilter';
import { PageLayout } from '../../PageLayout';
import { fileNameHelper } from '../../../utils/fileNameHelper';
import { PaymentRecipient, getPaymentRecipientsSubject, fetchPaymentRecipients } from '../../../store/PaymentRecipient';
import { Icon } from '../../Atoms/Icon';
import { TextSpan } from '../../Atoms/Text';
import { NewRecipientDialog } from './NewRecipientDialog';
import { hasEngineerOrHigherPermissions } from '../../../store/User';

const CustomToolbar = () => {
  const apiRef = useGridApiContext();

  const [densityMenuAnchor, setDensityMenuAnchor] = useState<null | HTMLElement>(null);
  const handleDensityChange = (density: 'compact' | 'standard' | 'comfortable') => {
    apiRef.current.setDensity(density);
    setDensityMenuAnchor(null);
  };

  return (
    <Toolbar>
      <Box
        sx={{
          padding: '1rem 0rem 1rem 1rem',
          gap: 1,
          flexWrap: 'wrap',
          display: 'flex',
          alignItems: 'left',
          width: '100%'
        }}>
        <CustomQuickFilter />
        <Box sx={{ marginLeft: 'auto', display: 'flex', gap: 0, flexWrap: 'wrap' }}>
          <Tooltip
            title="Density"
            enterDelay={200}>
            <Button
              startIcon={<FormatLineSpacingIcon fontSize="small" />}
              onClick={(e) => setDensityMenuAnchor(e.currentTarget)}
              sx={{ fontSize: '.8rem', color: 'secondary.main' }}>
              Density
            </Button>
          </Tooltip>

          <Menu
            anchorEl={densityMenuAnchor}
            open={Boolean(densityMenuAnchor)}
            onClose={() => setDensityMenuAnchor(null)}>
            <MenuItem onClick={() => handleDensityChange('compact')}>
              <FormatAlignJustifyIcon
                fontSize="small"
                sx={{ marginRight: 1 }}
              />
              Compact
            </MenuItem>
            <MenuItem onClick={() => handleDensityChange('standard')}>
              <DensityMediumIcon
                fontSize="small"
                sx={{ marginRight: 1 }}
              />
              Standard
            </MenuItem>
            <MenuItem onClick={() => handleDensityChange('comfortable')}>
              <DensitySmallIcon
                fontSize="small"
                sx={{ marginRight: 1 }}
              />
              Comfortable
            </MenuItem>
          </Menu>

          <Tooltip
            title="Download as CSV"
            enterDelay={200}>
            <ExportCsv
              options={{ fileName: fileNameHelper('Payment Recipients List') }}
              render={
                <Button
                  startIcon={<FileDownloadIcon fontSize="small" />}
                  sx={{ fontSize: '.8rem', color: 'secondary.main' }}>
                  Export
                </Button>
              }
            />
          </Tooltip>
        </Box>
      </Box>
    </Toolbar>
  );
};

export const PaymentRecipientsPage = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [rows, setRows] = useState<PaymentRecipient[]>([]);
  const [isNewDialogOpen, setIsNewDialogOpen] = useState<boolean>(false);

  const loadData = async () => {
    setIsLoading(true);
    await fetchPaymentRecipients();
    setIsLoading(false);
  };

  useEffect(() => {
    loadData();

    const recipientsSub = getPaymentRecipientsSubject().subscribe((a) => setRows(a));
    return () => {
      if (recipientsSub) recipientsSub.unsubscribe();
    };
  }, []);

  const sxStyle = {
    ...DEFAULT_TABLE_STYLES.sx,
    maxHeight: 'calc(100vh - 250px)',
    '.MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer': {
      marginLeft: 'auto'
    },
    borderWidth: '0',
    borderTopLeftRadius: '0',
    borderTopRightRadius: '0',
    borderBottomLeftRadius: '.75rem',
    borderBottomRightRadius: '.75rem'
  };

  const cardActions = () =>
    hasEngineerOrHigherPermissions() ? (
      <div className="space-x-6 flex">
        <button
          className="flex items-center space-x-2"
          onClick={() => setIsNewDialogOpen(true)}
          data-cy="new-recipient-button"
          data-testid="new-recipient-button">
          <Icon type="plus" />
          <TextSpan
            color="indigo"
            size="sm14">
            New Recipient
          </TextSpan>
        </button>
      </div>
    ) : undefined;

  return (
    <PageLayout
      className="w-full mt-6"
      dataTestId="payment-recipients-page"
      dataCy="payment-recipients-page"
      pageTitle="Payment Recipients">
      <div className="mb-16 -mt-16">
        <Link
          className="text-indigo inline-block relative"
          title="Fullscreen"
          to="/payment">
          <div className="flex items-center text-small">
            <ArrowBackIosNewIcon sx={{ fontSize: 14 }} />
            &nbsp; Payments
          </div>
        </Link>
      </div>
      <PageCard
        dataTestId="payment-recipients-list"
        dataCy="payment-recipients-list"
        childrenWrapperClass="max-w-[60vw] min-w-full"
        title="Recipients"
        cardActions={cardActions()}>
        <>
          {isLoading && (
            <div className="flex h-[10rem] m-auto">
              <ComponentSpinner label="Loading data..." />
            </div>
          )}
          {!isLoading && (
            <BasicDataGrid
              rows={rows}
              columns={DefaultRecipientGridColumnDefs}
              sxStyle={sxStyle}
              disableColumnMenu
              showColumnVerticalBorder
              showCellVerticalBorder
              slots={{ toolbar: CustomToolbar }}
              showToolbar
              primaryKey="id"
              slotProps={{ toolbar: { showSearch: true } }}
              hideFooterRowCount={false}
              initialState={{
                sorting: {
                  sortModel: [{ field: 'name', sort: 'asc' }]
                }
              }}
            />
          )}
        </>
        <NewRecipientDialog
          open={isNewDialogOpen}
          onClose={() => setIsNewDialogOpen(false)}
        />
      </PageCard>
    </PageLayout>
  );
};
